<template>
  <main-drawer :title="isEdit ? $tc('auth.edit_member') : $tc('auth.add_member')" @close="$emit('close')">
    <main-input v-model="form.firstName" :label="$tc('form.first_name')" :validator="$v.form.firstName"/>
    <main-input v-model="form.lastName" :label="$tc('form.last_name')" :validator="$v.form.lastName"/>
    <main-date-picker v-model="form.born" :label="$tc('form.date_of_birth')" :validator="$v.form.born"/>
    <main-select v-model="form.isMale" :label="$tc('form.gender')" :options="sexOptions" :validator="$v.form.isMale"/>
    <main-select v-model="form.belt" :label="$tc('form.belt')" :options="beltOptions" :validator="$v.form.belt"/>
    <template v-if="$feature.registration_fee">
      <main-checkbox v-if="!form.alwaysPays" v-model="form.noPay" :label="$tc('club.registration_fee_free')" />
      <main-checkbox v-if="user.isAdmin && !form.noPay" v-model="form.alwaysPays" :label="$tc('club.must_pay_registration_fee')" />
    </template>
    <main-upload-input v-model="image" :label="$tc('form.image')"/>
    <h4 class="section-title">{{ $tc('form.insurance') }}</h4>
    <main-input v-model="form.insuranceCompany" :label="$tc('form.insurance_company')" />
    <main-date-picker v-model="form.insuranceStartDate" :label="$tc('form.insurance_validity_start_date')" />
    <main-date-picker v-model="form.insuranceEndDate" :label="$tc('form.insurance_validity_end_date')" />
    <h4 class="section-title">{{ $tc('form.medical_examination') }}</h4>
    <main-date-picker v-model="form.medicalExaminationStartDate" :label="$tc('form.medical_examination_start_date')" />
    <main-date-picker v-model="form.medicalExaminationEndDate" :label="$tc('form.medical_examination_end_date')" />

    <h4 class="section-title">{{ $tc('general.documents') }}</h4>

    <main-upload-input v-model="insuranceDocumentFile" :label="$tc('form.insurance_document')" />
    <file
      v-if="!insuranceDocumentFile && member && member.insuranceDocument"
      :file="member.insuranceDocument"
      :url="$file('documents', member.insuranceDocument.path, member.insuranceDocument.id)" />
    <div v-if="insuranceDocumentError" class="field-error">{{ insuranceDocumentError }}</div>

    <main-upload-input v-model="medicalDocumentFile" :label="$tc('form.medical_document')" />
    <file
      v-if="!medicalDocumentFile && member && member.medicalExaminationDocument"
      :file="member.medicalExaminationDocument"
      :url="$file('documents', member.medicalExaminationDocument.path, member.medicalExaminationDocument.id)" />
    <div v-if="medicalDocumentError" class="field-error">{{ medicalDocumentError }}</div>

    <main-upload-input v-model="otherDocumentFiles" :label="$tc('form.other_documents')" multiple />
    <file
      v-for="doc in otherDocuments"
      :key="doc.id"
      :file="doc"
      :url="$file('documents', doc.path, doc.id)"
      deletable
      @delete="deleteOtherDocument(doc)" />

    <template v-slot:footer>
      <div class="flex-buttons">
        <el-button @click="$emit('close')">{{ $tc('general.cancel') }}</el-button>
        <el-button type="primary" :loading="loading" @click="save()">{{ isEdit ? $tc('general.edit') : $tc('general.add') }}</el-button>
      </div>
    </template>
  </main-drawer>
</template>

<script>
import { sexOptions, beltOptions } from '@/common/definitions'
import errorHandler from '@/common/errorHandler.js'
import { required } from 'vuelidate/lib/validators'
import File from '@/components/files/File'

export default {
  components: {
    File
  },

  props: {
    club: {
      type: Object,
      default: null
    },

    member: {
      type: Object,
      default: null
    },

    customAdd: {
      type: Boolean,
      default: null
    }
  },

  data () {
    return {
      loading: false,

      form: {
        firstName: null,
        lastName: null,
        born: null,
        isMale: null,
        belt: null,
        noPay: null,
        alwaysPays: null,
        earnedPoints: 0,
        insuranceCompany: null,
        insuranceStartDate: null,
        insuranceEndDate: null,
        medicalExaminationStartDate: null,
        medicalExaminationEndDate: null
      },

      sexOptions,
      beltOptions,
      image: null,

      insuranceDocumentFile: null,
      medicalDocumentFile: null,
      otherDocumentFiles: null,
      otherDocuments: [],
      insuranceDocumentError: null,
      medicalDocumentError: null
    }
  },

  computed: {
    user () {
      return this.$store.state.auth.user
    },

    isEdit () {
      return !!this.member
    },

    insuranceDatesChanged () {
      if (!this.isEdit) return !!(this.form.insuranceStartDate || this.form.insuranceEndDate)
      return this.form.insuranceStartDate !== (this.member.insuranceStartDate || null) ||
        this.form.insuranceEndDate !== (this.member.insuranceEndDate || null)
    },

    medicalDatesChanged () {
      if (!this.isEdit) return !!(this.form.medicalExaminationStartDate || this.form.medicalExaminationEndDate)
      return this.form.medicalExaminationStartDate !== (this.member.medicalExaminationStartDate || null) ||
        this.form.medicalExaminationEndDate !== (this.member.medicalExaminationEndDate || null)
    }
  },

  created () {
    if (this.isEdit) {
      this.form = {
        firstName: this.member.firstName,
        lastName: this.member.lastName,
        born: this.member.born,
        isMale: this.member.isMale,
        belt: this.member.belt,
        noPay: this.member.noPay,
        alwaysPays: this.member.alwaysPays,
        earnedPoints: this.member.earnedPoints ?? 0,
        insuranceCompany: this.member.insuranceCompany,
        insuranceStartDate: this.member.insuranceStartDate,
        insuranceEndDate: this.member.insuranceEndDate,
        medicalExaminationStartDate: this.member.medicalExaminationStartDate,
        medicalExaminationEndDate: this.member.medicalExaminationEndDate
      }
      this.otherDocuments = (this.member.documents || []).filter(doc => doc.type === 'other')
    }
  },

  methods: {
    validateDocuments () {
      this.insuranceDocumentError = null
      this.medicalDocumentError = null
      let valid = true

      if (this.insuranceDatesChanged && !this.insuranceDocumentFile) {
        this.insuranceDocumentError = this.$tc('messages.insurance_document_required')
        valid = false
      }

      if (this.medicalDatesChanged && !this.medicalDocumentFile) {
        this.medicalDocumentError = this.$tc('messages.medical_document_required')
        valid = false
      }

      return valid
    },

    async deleteOtherDocument (doc) {
      try {
        await this.$store.dispatch('memberDocuments/delete', doc.id)
        this.otherDocuments = this.otherDocuments.filter(d => d.id !== doc.id)
      } catch (error) {
        console.error(error)
        errorHandler(error)
      }
    },

    async uploadDocument (file, type, memberId) {
      const doc = await this.$store.dispatch('memberDocuments/create', {
        memberId,
        type,
        name: file.name,
        size: file.size
      })
      await this.$store.dispatch('memberDocuments/upload', { id: doc.id, file })
      return doc
    },

    async save () {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return
      if (this.loading) return

      if (this.customAdd) return this.$emit('custom-add', this.form)

      if (!this.validateDocuments()) return

      this.loading = true
      try {
        const {
          insuranceStartDate,
          insuranceEndDate,
          medicalExaminationStartDate,
          medicalExaminationEndDate,
          ...baseForm
        } = this.form

        let member
        if (this.isEdit) {
          member = await this.$store.dispatch('members/update', {
            id: this.member.id,
            payload: baseForm
          })
        } else {
          member = await this.$store.dispatch('members/create', {
            clubId: this.club.id,
            ...baseForm
          })
        }

        if (this.image) {
          member = await this.$store.dispatch('members/upload', {
            id: member.id,
            file: this.image
          })
        }

        const dateUpdate = {}

        if (this.insuranceDocumentFile) {
          const doc = await this.uploadDocument(this.insuranceDocumentFile, 'insurance', member.id)
          dateUpdate.insuranceStartDate = insuranceStartDate
          dateUpdate.insuranceEndDate = insuranceEndDate
          dateUpdate.insuranceDocument = doc.id
        }

        if (this.medicalDocumentFile) {
          const doc = await this.uploadDocument(this.medicalDocumentFile, 'medical', member.id)
          dateUpdate.medicalExaminationStartDate = medicalExaminationStartDate
          dateUpdate.medicalExaminationEndDate = medicalExaminationEndDate
          dateUpdate.medicalExaminationDocument = doc.id
        }

        if (Object.keys(dateUpdate).length) {
          member = await this.$store.dispatch('members/update', {
            id: member.id,
            payload: dateUpdate
          })
        }

        if (this.otherDocumentFiles?.length) {
          await Promise.all(this.otherDocumentFiles.map(file => this.uploadDocument(file, 'other', member.id)))
          member = await this.$store.dispatch('members/get', member.id)
        }

        this.$message.success(this.isEdit ? this.$tc('messages.success_updated_member') : this.$tc('messages.success_created_member'))
        this.$emit(this.isEdit ? 'edit' : 'add', member)
      } catch (error) {
        console.error(error)
        errorHandler(error)
      } finally {
        this.loading = false
      }
    }
  },

  validations () {
    return {
      form: {
        firstName: { required },
        lastName: { required },
        born: { required },
        isMale: { required },
        belt: { required }
      }
    }
  }
}
</script>

<style scoped>
.section-title {
  margin: 16px 0 8px;
  font-size: 16px;
  font-weight: 600;
}

.field-error {
  color: #f56c6c;
  font-size: 12px;
  margin: -8px 0 12px;
}
</style>
